import Plotly from "plotly.js-dist-min";
import store from "@/store";
import { QA_QC_BLUE } from "./distributionPlotMapper.js";
import {
  applyPlotlyGridLineStroke,
  chartGridColor,
  CHART_GRID_COLOR_LIGHT_RGBA,
  mergePlotlyTheme,
  plotlyThemeColors,
} from "@/utils/chartTheme";

function isDarkMode() {
  if (typeof document !== "undefined") {
    const vApp = document.querySelector("#app .v-application");
    if (vApp?.classList.contains("dark-mode")) return true;
    if (vApp?.classList.contains("light-mode")) return false;
    if (document.body?.classList.contains("dark-mode")) return true;
    if (document.body?.classList.contains("light-mode")) return false;
  }
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem("darkMode") : null;
  if (stored === "true") return true;
  if (stored === "false") return false;
  try {
    return !!store.getters["darkMode/isDarkMode"];
  } catch {
    return false;
  }
}

const HIST_COLOR_DEFAULT = QA_QC_BLUE;
const HIST_LINE_COLOR = "transparent";
const HIST_LINE_WIDTH = 0;
export const QA_QC_DIST_PLOT_HEIGHT = 320;

const BAR_PALETTE_FALLBACK = ["#22c55e", "#f59e0b", "#ef4444"];

function axisLineColor(dark) {
  return dark ? "#9ca3af" : "#667085";
}

function applyDistributionXAxisLayout(layout, categories, xTitle) {
  const dark = isDarkMode();
  const theme = plotlyThemeColors(dark);
  const lineColor = axisLineColor(dark);
  const cats = Array.isArray(categories) ? categories.map((c) => String(c ?? "")) : [];
  const nCat = cats.length;
  const maxLen = cats.reduce((m, c) => Math.max(m, c.length), 0);

  layout.xaxis.title =
    xTitle && String(xTitle).trim()
      ? { text: String(xTitle).trim(), standoff: 10, font: { size: 12, color: theme.title } }
      : { text: "" };
  layout.xaxis.automargin = true;
  layout.xaxis.tickfont = {
    size: 12,
    color: theme.title,
    family: "Inter, PT Sans, sans-serif",
  };
  layout.xaxis.showline = true;
  layout.xaxis.linecolor = lineColor;
  layout.xaxis.linewidth = 1.5;
  layout.xaxis.mirror = false;
  layout.xaxis.zeroline = false;

  layout.yaxis.showline = true;
  layout.yaxis.linecolor = lineColor;
  layout.yaxis.linewidth = 1.5;
  layout.yaxis.mirror = false;
  layout.yaxis.tickfont = { size: 11, color: theme.tick, family: "Inter, PT Sans, sans-serif" };

  if (nCat <= 3 && maxLen <= 26) {
    layout.xaxis.tickangle = 0;
    layout.margin.b = Math.max(layout.margin.b || 48, 52);
  } else if (nCat > 6 || maxLen > 18) {
    layout.xaxis.tickangle = -40;
    layout.margin.b = Math.max(layout.margin.b || 56, Math.min(128, 56 + Math.round(maxLen * 1.8)));
  } else {
    layout.xaxis.tickangle = -32;
    layout.margin.b = Math.max(layout.margin.b || 56, 72);
  }
}

function yGridColor(dark) {
  return dark ? chartGridColor(true) : CHART_GRID_COLOR_LIGHT_RGBA;
}

function finishPlotlyDraw(el) {
  applyPlotlyGridLineStroke(el, isDarkMode());
}

/** Matches Studio / dashboard histogram Y-axis label */
const QA_QC_DIST_Y_AXIS_DEFAULT = "No. of Samples";

function resolveDistributionYAxisTitle(viewData) {
  const t = viewData?.yAxisTitle;
  if (t != null && String(t).trim() !== "") return String(t).trim();
  return QA_QC_DIST_Y_AXIS_DEFAULT;
}

function resolveDistributionXAxisTitle(viewData) {
  const t = viewData?.xAxisTitle;
  if (t != null && String(t).trim() !== "") return String(t).trim();
  return "";
}

function histogramNumericValues(rawValues) {
  if (!Array.isArray(rawValues)) return [];
  return rawValues
    .map((v) => (typeof v === "string" ? parseFloat(v) : v))
    .filter((v) => Number.isFinite(v));
}

function uniformHistogramValue(values) {
  if (!Array.isArray(values) || values.length === 0) return null;
  const first = values[0];
  if (!Number.isFinite(first)) return null;
  for (let i = 1; i < values.length; i += 1) {
    if (values[i] !== first) return null;
  }
  return first;
}

function formatHistogramValueLabel(value) {
  if (!Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function histogramXBins(sortedData, binCount) {
  const n = Math.max(2, Math.min(100, Number(binCount) || 10));
  const minVal = Math.min(...sortedData);
  const maxVal = Math.max(...sortedData);
  const range = maxVal - minVal;
  if (range <= 0) {
    const pad = Math.max(Math.abs(minVal) * 1e-6, 1e-6);
    return {
      start: minVal - pad,
      end: maxVal + pad,
      size: (2 * pad) / n,
    };
  }
  const adjustedMinVal = Math.max(0, minVal);
  const adjustedRange = maxVal - adjustedMinVal;
  const padding = adjustedRange * 0.001;
  const binSize = (adjustedRange + 2 * padding) / n;
  return {
    start: Math.max(0, adjustedMinVal - padding),
    end: maxVal + padding,
    size: binSize,
  };
}

function barColorsForCategories(categories) {
  let idx = 0;
  return categories.map((cat) => {
    const s = String(cat || "");
    if (/N\/A|not reported/i.test(s)) return "#9ca3af";
    const c = BAR_PALETTE_FALLBACK[Math.min(idx, BAR_PALETTE_FALLBACK.length - 1)];
    idx += 1;
    return c;
  });
}

function summaryAnnotations(summaryStats) {
  if (!summaryStats?.rows?.length) return [];
  const accent = summaryStats.accentColor || QA_QC_BLUE;
  const dark = isDarkMode();
  const theme = plotlyThemeColors(dark);
  const backgroundColor = dark ? "#1E2835" : "#F7F7F7";
  const borderColor = dark ? "#2F383E" : "#DFDFDF";
  const html = summaryStats.rows
    .map((row) => {
      let val;
      if (row.valueColor !== undefined) {
        val = `<span style="color:${row.valueColor};font-weight:${row.emphasize ? 600 : 400}">${row.value}</span>`;
      } else if (row.emphasize) {
        val = `<span style="color:${accent};font-weight:600">${row.value}</span>`;
      } else {
        val = `<span>${row.value}</span>`;
      }
      return `${row.label} ${val}`;
    })
    .join("<br>");
  return [
    {
      xref: "paper",
      yref: "paper",
      x: 0.99,
      y: 0.97,
      xanchor: "right",
      yanchor: "top",
      text: html,
      showarrow: false,
      align: "left",
      bgcolor: backgroundColor,
      bordercolor: borderColor,
      borderwidth: 1,
      borderpad: 8,
      font: { size: 11, color: theme.tick },
    },
  ];
}

function baseHistogramLayout(xTitle, yAxisTitle = "No. of Samples") {
  const dark = isDarkMode();
  const lineColor = axisLineColor(dark);
  return mergePlotlyTheme(
    {
      autosize: true,
      height: QA_QC_DIST_PLOT_HEIGHT,
      margin: { t: 12, r: 16, b: 48, l: 52 },
      showlegend: false,
      bargap: 0.12,
      xaxis: {
        title: { text: xTitle, standoff: 8 },
        tickangle: -45,
        tickfont: { size: 11 },
        showgrid: false,
        zeroline: false,
        showline: true,
        linecolor: lineColor,
        linewidth: 1,
        mirror: false,
      },
      yaxis: {
        title: { text: yAxisTitle, standoff: 12 },
        tickfont: { size: 11 },
        rangemode: "tozero",
        showgrid: true,
        gridcolor: yGridColor(dark),
        gridwidth: 1,
        griddash: "dot",
        zeroline: true,
        zerolinecolor: lineColor,
        showline: true,
        linecolor: lineColor,
        linewidth: 1,
        mirror: false,
      },
    },
    dark
  );
}

function baseCategoricalLayout(xTitle, yAxisTitle = "No. of Samples") {
  const dark = isDarkMode();
  const lineColor = axisLineColor(dark);
  return mergePlotlyTheme(
    {
      autosize: true,
      height: QA_QC_DIST_PLOT_HEIGHT,
      margin: { t: 12, r: 16, b: 56, l: 52 },
      showlegend: false,
      bargap: 0.22,
      xaxis: {
        title: { text: xTitle, standoff: 8 },
        tickangle: -35,
        tickfont: { size: 10 },
        type: "category",
        categoryorder: "array",
        showgrid: false,
        showline: true,
        linecolor: lineColor,
      },
      yaxis: {
        title: { text: yAxisTitle, standoff: 12 },
        tickfont: { size: 11 },
        rangemode: "tozero",
        showgrid: true,
        gridcolor: yGridColor(dark),
        gridwidth: 1,
        griddash: "dot",
        zeroline: true,
        zerolinecolor: lineColor,
        showline: true,
        linecolor: lineColor,
      },
    },
    dark
  );
}

export function drawQaqcDistributionPlotly(el, viewData, numBins) {
  if (!el || !viewData) return Promise.resolve();

  const kind = viewData.plotKind;
  const xTitle = resolveDistributionXAxisTitle(viewData);
  const yAxisTitle = resolveDistributionYAxisTitle(viewData);
  const config = { responsive: true, displayModeBar: false };

  if (kind === "histogram") {
    const raw = viewData.rawValues || [];
    if (raw.length === 0) {
      el.innerHTML = "";
      return Promise.resolve();
    }
    const sortedData = histogramNumericValues(raw).sort((a, b) => a - b);
    if (!sortedData.length) {
      el.innerHTML = "";
      return Promise.resolve();
    }
    const barColor = viewData.histogramBarColor || HIST_COLOR_DEFAULT;
    const layout = baseHistogramLayout(xTitle, yAxisTitle);
    applyDistributionXAxisLayout(layout, [], xTitle);
    const sameValue = uniformHistogramValue(sortedData);
    let trace;
    if (sameValue !== null) {
      const valueLabel = formatHistogramValueLabel(sameValue);
      trace = {
        x: [valueLabel],
        y: [sortedData.length],
        type: "bar",
        name: xTitle || "Histogram",
        marker: {
          color: barColor,
          line: { color: HIST_LINE_COLOR, width: HIST_LINE_WIDTH },
        },
        hovertemplate: `${xTitle || "Value"}: ${valueLabel}<br>No. of Samples: %{y}<extra></extra>`,
      };
      layout.xaxis.type = "category";
      layout.xaxis.tickangle = 0;
      layout.xaxis.categoryorder = "array";
      layout.xaxis.categoryarray = [valueLabel];
      layout.bargap = 0.6;
    } else {
      const xbins = histogramXBins(sortedData, numBins);
      trace = {
        x: sortedData,
        type: "histogram",
        name: xTitle || "Histogram",
        autobinx: false,
        nbinsx: Math.max(2, Math.min(100, Number(numBins) || 10)),
        marker: {
          color: barColor,
          line: { color: HIST_LINE_COLOR, width: HIST_LINE_WIDTH },
        },
        hovertemplate: "Range: %{x}<br>No. of Samples: %{y}<extra></extra>",
      };
      if (xbins) trace.xbins = xbins;
    }
    const ann = summaryAnnotations(viewData.summaryStats);
    if (ann.length) {
      layout.annotations = ann;
    }
    return Plotly.react(el, [trace], layout, config).then(() => finishPlotlyDraw(el));
  }

  if (kind === "categoricalBar") {
    const categories = viewData.categories || [];
    const counts = viewData.categoryCounts || [];
    if (categories.length === 0) {
      el.innerHTML = "";
      return Promise.resolve();
    }
    const explicit = viewData.barColors || [];
    const colors =
      explicit.length === categories.length ? explicit : barColorsForCategories(categories);
    const trace = {
      x: categories,
      y: counts,
      type: "bar",
      marker: {
        color: colors,
        line: { width: 0 },
      },
      hovertemplate: "%{x}<br>No. of Samples: %{y}<extra></extra>",
    };
    const layout = baseCategoricalLayout(xTitle, yAxisTitle);
    applyDistributionXAxisLayout(layout, categories, xTitle);
    layout.hovermode = "closest";
    layout.xaxis.categoryorder = "array";
    layout.xaxis.categoryarray = categories;
    if (categories.length === 2) {
      layout.bargap = 0.45;
    }
    const ann = summaryAnnotations(viewData.summaryStats);
    if (ann.length) {
      layout.annotations = ann;
    }
    return Plotly.react(el, [trace], layout, config).then(() => finishPlotlyDraw(el));
  }

  el.innerHTML = "";
  return Promise.resolve();
}
